import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, IconButton, Tooltip } from "@mui/material";
import { green } from "@mui/material/colors";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import axios from "axios";
import DisasterSiteAddModal from "./DisasterSiteAddModal";
import RdvAttendanceQRModal from "./RdvAttendanceQRModal";
import markerSiteIcon from "../../icon/marker-site.svg";

export default function DisasterSiteList(props) {
  const [sites, setSites] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [qrSite, setQrSite] = useState(null);
  const [attendanceCounts, setAttendanceCounts] = useState({});
  const ondsid = typeof sessionStorage !== "undefined" ? (sessionStorage.ondsid || "").trim() : "";
  const dsnm = typeof sessionStorage !== "undefined" ? (sessionStorage.onname || "").trim() : "";

  const fetchAttendanceCounts = useCallback(async () => {
    if (!ondsid || !props.apiAddr || sites.length === 0) {
      setAttendanceCounts({});
      return;
    }
    const next = {};
    await Promise.all(
      sites.map(async (site) => {
        try {
          const res = await fetch(
            `${props.apiAddr}/get/rdvattendance?dsid=${encodeURIComponent(ondsid)}&site_id=${site.snum}`,
          );
          const list = await res.json();
          const arr = Array.isArray(list) ? list : [];
          next[site.snum] = arr.filter((r) => !r.check_out_dt).length;
        } catch (e) {
          next[site.snum] = 0;
        }
      }),
    );
    setAttendanceCounts((prev) => ({ ...prev, ...next }));
  }, [ondsid, props.apiAddr, sites]);

  useEffect(() => {
    fetchAttendanceCounts();
  }, [fetchAttendanceCounts]);

  useEffect(() => {
    const onAttendanceRefresh = (e) => {
      const eventDsid = (e?.detail?.dsid || "").toString().trim();
      if (!eventDsid || eventDsid === ondsid) fetchAttendanceCounts();
    };
    window.addEventListener("rdvAttendanceRefresh", onAttendanceRefresh);
    return () => window.removeEventListener("rdvAttendanceRefresh", onAttendanceRefresh);
  }, [ondsid, fetchAttendanceCounts]);

  /** 검은색 채움 원문자: 1~10 ❶…❿, 11~20 ⓫…⓴, 그 이상 (n) */
  const getCircledNum = (n) => {
    if (n >= 1 && n <= 10) return String.fromCodePoint(0x2776 + n - 1);
    if (n >= 11 && n <= 20) return String.fromCodePoint(0x24eb + n - 11);
    return `(${n})`;
  };

  useEffect(() => {
    if (typeof props.onSitesCountChange === "function") props.onSitesCountChange(sites.length);
  }, [sites.length, props.onSitesCountChange]);

  const loadSitesFromStorage = () => {
    try {
      const s = sessionStorage.dsSites;
      if (s) {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) setSites(arr);
      }
    } catch (e) {}
  };

  const fetchSites = async () => {
    if (!ondsid || !props.apiAddr) return;
    try {
      const res = await fetch(`${props.apiAddr}/get/dsites?dsid=${ondsid}`);
      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];
      setSites(arr);
      sessionStorage.dsSites = JSON.stringify(arr);
    } catch (e) {
      console.warn("dsites fetch failed", e);
      setSites([]);
    }
  };

  useEffect(() => {
    if (ondsid) {
      loadSitesFromStorage();
      fetchSites();
    } else setSites([]);
  }, [ondsid, props.apiAddr]);

  const handleSiteSelect = (site) => {
    sessionStorage.ondslat = (site.slat || "").toString().trim();
    sessionStorage.ondslon = (site.slon || "").toString().trim();
    sessionStorage.onsiteid = (site.snum != null ? site.snum : "").toString();
    if (props.mapfresh) props.mapfresh();
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    fetchSites();
    if (props.onSelectDS) props.onSelectDS();
    if (props.mapfresh) props.mapfresh();
  };

  const handleDelete = async (e, site) => {
    e.stopPropagation();
    if (!window.confirm(`"${site.sname || '이 현장'}"을 삭제할까요?`)) return;
    try {
      await axios.post(`${props.apiAddr}/post/delsite`, {
        snum: site.snum,
        dsid: ondsid,
      });
      const updated = sites.filter((s) => s.snum !== site.snum);
      setSites(updated);
      sessionStorage.dsSites = JSON.stringify(updated);
      if (props.onSelectDS) props.onSelectDS();
    } catch (err) {
      console.error("delsite failed", err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  if (!ondsid) return null;

  return (
    <Box sx={{ mt: 1, p: 1, backgroundColor: green[50], borderRadius: 1, border: "1px solid", borderColor: green[200], ...(props.sx || {}) }}>
      {/* 헤더 */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="img" src={markerSiteIcon} alt="" sx={{ width: 20, height: 20, display: "block" }} />
          재난현장 관리
        </Typography>
        <Button
          size="small"
          startIcon={<AddLocationIcon />}
          onClick={() => setShowAddModal(true)}
          sx={{ fontSize: "0.75rem", minWidth: 0, py: 0.25 }}
        >
          재난현장 추가
        </Button>
      </Box>

      {/* 목록 */}
      {sites.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          등록된 현장이 없습니다.
        </Typography>
      ) : (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0.5,
          maxHeight: 160,
          overflowY: "auto",
        }}>
          {sites.map((site, index) => (
            <Box
              key={site.snum}
              sx={{
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: sessionStorage.onsiteid === String(site.snum) ? green[700] : green[300],
                borderRadius: 1,
                overflow: "hidden",
                "&:hover": { borderColor: green[600] },
              }}
            >
              {/* 1행: 현장명 버튼 */}
              <Tooltip
                title={site.saddr || "(주소 없음)"}
                placement="top"
                enterDelay={300}
                componentsProps={{
                  popper: { modifiers: [{ name: "offset", options: { offset: [0, -10] } }] },
                }}
              >
                <Button
                  size="small"
                  fullWidth
                  onClick={() => handleSiteSelect(site)}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    py: 0.5,
                    minHeight: 32,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    bgcolor: sessionStorage.onsiteid === String(site.snum) ? green[200] : "transparent",
                    "&:hover": {
                      bgcolor: sessionStorage.onsiteid === String(site.snum) ? green[300] : green[100],
                    },
                  }}
                >
                  {getCircledNum(index + 1)} {site.sname || "(이름 없음)"}
                </Button>
              </Tooltip>

              {/* 2행: 출퇴근수 | QR버튼 | 삭제버튼 */}
              <Box sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "stretch",
                gap: 0,
                borderTop: "1px solid",
                borderColor: green[200],
              }}>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0, py: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: green[800] }}>
                    {attendanceCounts[site.snum] ?? "—"}
                  </Typography>
                </Box>
                <Tooltip title="출퇴근 QR" placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setQrSite(site); }}
                    sx={{
                      flex: 1,
                      borderRadius: 0,
                      color: green[700],
                      "&:hover": { bgcolor: green[100] },
                    }}
                  >
                    <QrCode2Icon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="현장 삭제" placement="top">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDelete(e, site)}
                    sx={{
                      flex: 1,
                      borderRadius: 0,
                      "&:hover": { bgcolor: green[100] },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* 재난현장 추가 모달 */}
      {showAddModal && (
        <DisasterSiteAddModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
          apiAddr={props.apiAddr}
          dsid={ondsid}
        />
      )}

      {/* 출퇴근 QR 모달 */}
      {qrSite && (
        <RdvAttendanceQRModal
          open={!!qrSite}
          onClose={() => {
            setQrSite(null);
            fetchAttendanceCounts();
          }}
          dsid={ondsid}
          siteId={qrSite.snum}   /* 재난현장 모드: rdvId 대신 siteId 사용 */
          rname={qrSite.sname || ""}
          dsnm={dsnm}
          apiAddr={props.apiAddr}
        />
      )}
    </Box>
  );
}
